import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Icon, IconName } from '../components/icons/Icon';

type Role = 'text' | 'image' | 'audio';
interface Work {
    id: string; content_type: Role; title: string | null; description: string | null;
    file_url: string | null; image_url: string | null; duration_sec: number | null; char_count: number | null;
    cached_like_count: number; included: number; sort_order: number | null;
    tool: string | null; role_scope: string | null; voice_tags: string | null; recording_spec: string | null; intent_note: string | null;
}
const ROLE: Record<Role, { label: string; icon: IconName; bg: string }> = {
    text: { label: 'テキスト', icon: 'script', bg: 'bg-role-script' },
    image: { label: '絵', icon: 'art', bg: 'bg-role-illustration' },
    audio: { label: '声', icon: 'voice', bg: 'bg-role-voice' },
};
const fmtDur = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const readMin = (c: number) => Math.max(1, Math.round(c / 500));

export const Portfolio = () => {
    const [loading, setLoading] = useState(true);
    const [, setMe] = useState<string | null>(null);
    const [headline, setHeadline] = useState('');
    const [availability, setAvailability] = useState('');
    const [contactX, setContactX] = useState('');
    const [visibility, setVisibility] = useState('private');
    const [mode, setMode] = useState<'normal' | 'sales'>('normal');
    const [works, setWorks] = useState<Work[]>([]);
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        api.getCurrentUser().then(r => { if (r.success && r.data?.id) setMe(r.data.id); });
        api.getMyPortfolio().then((d) => {
            if (d) {
                const p = d.profile || {};
                setHeadline(p.headline || ''); setAvailability(p.availability || '');
                setVisibility(p.visibility || 'private'); setMode(p.default_mode === 'sales' ? 'sales' : 'normal');
                try { const c = p.contact_links ? JSON.parse(p.contact_links) : {}; setContactX(c.x || ''); } catch { /* noop */ }
                setWorks((d.works || []).map((w: any) => ({ ...w, included: w.included === null || w.included === undefined ? 1 : w.included })));
            }
        }).finally(() => setLoading(false));
    }, []);

    const saveSettings = async () => {
        const ok = await api.savePortfolio({ headline, availability, visibility, defaultMode: mode, contactLinks: { x: contactX } });
        toast({ title: ok ? '設定を保存しました' : '保存に失敗しました' });
    };
    const patchWork = (id: string, patch: Partial<Work>) => setWorks(ws => ws.map(w => w.id === id ? { ...w, ...patch } : w));
    const saveItem = async (w: Work) => {
        const ok = await api.savePortfolioItem(w.id, {
            included: !!w.included, sortOrder: w.sort_order || 0, tool: w.tool, roleScope: w.role_scope,
            voiceTags: w.voice_tags, recordingSpec: w.recording_spec, intentNote: w.intent_note,
        });
        toast({ title: ok ? '保存しました' : '保存に失敗しました' });
    };

    const move = async (r: Role, idx: number, dir: number) => {
        const list = works.filter(w => w.content_type === r).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const j = idx + dir;
        if (j < 0 || j >= list.length) return;
        const tmp = list[idx]; list[idx] = list[j]; list[j] = tmp;
        const reordered = list.map((w, i) => ({ ...w, sort_order: i }));
        setWorks(ws => ws.map(w => { const u = reordered.find(x => x.id === w.id); return u ? { ...w, sort_order: u.sort_order } : w; }));
        for (const w of reordered) {
            await api.savePortfolioItem(w.id, { included: !!w.included, sortOrder: w.sort_order, tool: w.tool, roleScope: w.role_scope, voiceTags: w.voice_tags, recordingSpec: w.recording_spec, intentNote: w.intent_note });
        }
    };
    const byRole = (r: Role) => works.filter(w => w.content_type === r).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const included = works.filter(w => w.included);
    const best = [...included].sort((a, b) => b.cached_like_count - a.cached_like_count).slice(0, 3);

    if (loading) return <div className="py-20 text-center text-primary font-display font-bold animate-pulse">読み込み中...</div>;

    return (
        <div className="max-w-4xl mx-auto px-6 pt-10 pb-24">
            <style>{`@media print { body * { visibility: hidden !important; } #pf-print, #pf-print * { visibility: visible !important; } #pf-print { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } }`}</style>

            <div className="flex items-center justify-between mb-6 no-print">
                <div>
                    <p className="font-display font-bold tracking-[0.18em] text-pop text-sm">PORTFOLIO</p>
                    <h1 className="font-display font-black text-3xl text-ink">ポートフォリオ</h1>
                    <p className="text-ink-2 text-sm mt-1">自分の投稿から作成。本人のみ閲覧できます。</p>
                </div>
                <div className="flex gap-2">
                    <button className={`btn btn-sm ${preview ? 'btn-ghost' : 'btn-primary'}`} onClick={() => setPreview(false)}>編集</button>
                    <button className={`btn btn-sm ${preview ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPreview(true)}>プレビュー</button>
                    {preview && <button className="btn btn-sm btn-accent" onClick={() => window.print()}><Icon name="arrow-right" size={14} /> 印刷/PDF</button>}
                </div>
            </div>

            {!preview ? (
                <div className="space-y-6 no-print">
                    {/* 設定 */}
                    <div className="pop-card p-5 space-y-3">
                        <div className="font-display font-bold text-ink">基本情報</div>
                        <input className="pop-field w-full" placeholder="キャッチコピー（例: 企業VP専門ナレーター）" value={headline} onChange={e => setHeadline(e.target.value)} />
                        <input className="pop-field w-full" placeholder="稼働状況（例: 平日夜に対応可）" value={availability} onChange={e => setAvailability(e.target.value)} />
                        <input className="pop-field w-full" placeholder="連絡先 X (例: @anokoe)" value={contactX} onChange={e => setContactX(e.target.value)} />
                        <div className="flex flex-wrap gap-3 items-center">
                            <label className="text-sm text-ink-2">公開範囲:</label>
                            <select className="pop-field" value={visibility} onChange={e => setVisibility(e.target.value)}>
                                <option value="private">本人のみ</option>
                                <option value="supporters_only">支援者限定（準備中）</option>
                                <option value="public">全体公開（準備中）</option>
                            </select>
                            <label className="text-sm text-ink-2 ml-2">表示モード:</label>
                            <select className="pop-field" value={mode} onChange={e => setMode(e.target.value as any)}>
                                <option value="normal">通常</option>
                                <option value="sales">営業</option>
                            </select>
                            <button className="btn btn-sm btn-primary ml-auto" onClick={saveSettings}>設定を保存</button>
                        </div>
                    </div>

                    {/* ロール別 作品キュレーション */}
                    {(['audio', 'image', 'text'] as Role[]).map(r => {
                        const list = byRole(r);
                        if (list.length === 0) return null;
                        return (
                            <div key={r} className="pop-card p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className={`w-7 h-7 rounded-full border-2 border-ink ${ROLE[r].bg} text-white flex items-center justify-center`}><Icon name={ROLE[r].icon} size={14} /></span>
                                    <span className="font-display font-bold text-ink">{ROLE[r].label}（{list.length}）</span>
                                </div>
                                <div className="space-y-3">
                                    {list.map((w, idx) => (
                                        <div key={w.id} className="border-2 border-cream-2 rounded-pop-sm p-3">
                                            <div className="flex items-center gap-2">
                                                <span className="flex flex-col leading-none text-ink-3">
                                                    <button type="button" className="hover:text-ink text-[10px]" onClick={() => move(r, idx, -1)} aria-label="上へ">▲</button>
                                                    <button type="button" className="hover:text-ink text-[10px]" onClick={() => move(r, idx, 1)} aria-label="下へ">▼</button>
                                                </span>
                                                <label className="flex items-center gap-1.5 text-sm font-bold text-ink cursor-pointer">
                                                    <input type="checkbox" checked={!!w.included} onChange={() => patchWork(w.id, { included: w.included ? 0 : 1 })} /> 載せる
                                                </label>
                                                <span className="font-display font-bold text-ink truncate flex-1">{w.title || '無題'}</span>
                                                <span className="text-xs text-pop font-bold inline-flex items-center gap-1"><Icon name="heart" size={13} /> {w.cached_like_count}</span>
                                                {r === 'audio' && w.duration_sec ? <span className="text-xs text-ink-3">{fmtDur(w.duration_sec)}</span> : null}
                                                {r === 'text' && w.char_count ? <span className="text-xs text-ink-3">{w.char_count}字 / 約{readMin(w.char_count)}分</span> : null}
                                            </div>
                                            <textarea className="pop-field w-full mt-2 text-sm" rows={2} placeholder="制作意図・こだわり" value={w.intent_note || ''} onChange={e => patchWork(w.id, { intent_note: e.target.value })} />
                                            {r === 'image' && (
                                                <div className="flex gap-2 mt-2">
                                                    <input className="pop-field flex-1 text-sm" placeholder="使用ツール" value={w.tool || ''} onChange={e => patchWork(w.id, { tool: e.target.value })} />
                                                    <input className="pop-field flex-1 text-sm" placeholder="担当範囲" value={w.role_scope || ''} onChange={e => patchWork(w.id, { role_scope: e.target.value })} />
                                                </div>
                                            )}
                                            {r === 'audio' && (
                                                <div className="flex gap-2 mt-2">
                                                    <input className="pop-field flex-1 text-sm" placeholder="声質タグ（例: 落ち着いた/20代男性）" value={w.voice_tags || ''} onChange={e => patchWork(w.id, { voice_tags: e.target.value })} />
                                                    <input className="pop-field flex-1 text-sm" placeholder="録音仕様（例: 48kHz/24bit）" value={w.recording_spec || ''} onChange={e => patchWork(w.id, { recording_spec: e.target.value })} />
                                                </div>
                                            )}
                                            <div className="text-right mt-2"><button className="btn btn-sm btn-ghost" onClick={() => saveItem(w)}>この作品を保存</button></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {works.length === 0 && <div className="pop-card p-8 text-center text-ink-3">まだ投稿がありません。作品を投稿するとここに集約されます。</div>}
                </div>
            ) : (
                // ===== プレビュー =====
                <div id="pf-print" className="space-y-8">
                    <div className="pop-card p-6 text-center">
                        <p className="font-display font-bold tracking-[0.16em] text-pop text-sm">{mode === 'sales' ? 'CREATOR PORTFOLIO' : 'MY WORKS'}</p>
                        <h2 className="font-display font-black text-2xl text-ink mt-1">{headline || '（キャッチコピー未設定）'}</h2>
                        {availability && <p className="text-ink-2 text-sm mt-1">{availability}</p>}
                        {contactX && <p className="text-ink-2 text-sm mt-1">X: {contactX}</p>}
                        {best.length > 0 && (
                            <div className="flex justify-center gap-2 mt-4 flex-wrap">
                                {best.map(w => (
                                    <span key={w.id} className="pop-tag inline-flex items-center gap-1"><Icon name={ROLE[w.content_type].icon} size={12} /> {w.title || '無題'} · ♥{w.cached_like_count}</span>
                                ))}
                            </div>
                        )}
                    </div>
                    {(['audio', 'image', 'text'] as Role[]).map(r => {
                        const list = byRole(r).filter(w => w.included);
                        if (list.length === 0) return null;
                        return (
                            <div key={r}>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className={`w-7 h-7 rounded-full border-2 border-ink ${ROLE[r].bg} text-white flex items-center justify-center`}><Icon name={ROLE[r].icon} size={14} /></span>
                                    <span className="font-display font-bold text-ink">{ROLE[r].label}</span>
                                </div>
                                <div className={r === 'image' ? 'grid grid-cols-2 sm:grid-cols-3 gap-3' : 'space-y-3'}>
                                    {list.map(w => (
                                        <div key={w.id} className="pop-card p-4">
                                            {r === 'image' && (w.image_url || w.file_url) && <img src={w.image_url || w.file_url || ''} alt={w.title || ''} className="w-full h-32 object-cover rounded-pop-sm border-2 border-ink mb-2" />}
                                            <div className="font-display font-bold text-ink text-sm">{w.title || '無題'}</div>
                                            <div className="text-xs text-ink-3 mt-0.5">
                                                {r === 'audio' && w.duration_sec ? `${fmtDur(w.duration_sec)}` : ''}
                                                {r === 'audio' && w.voice_tags ? ` · ${w.voice_tags}` : ''}
                                                {r === 'text' && w.char_count ? `${w.char_count}字 / 約${readMin(w.char_count)}分` : ''}
                                                {r === 'image' && w.tool ? `${w.tool}` : ''}
                                                {r === 'image' && w.role_scope ? ` · ${w.role_scope}` : ''}
                                                {` · ♥${w.cached_like_count}`}
                                            </div>
                                            {w.intent_note && <p className="text-xs text-ink-2 mt-1 leading-relaxed">{w.intent_note}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {included.length === 0 && <div className="pop-card p-8 text-center text-ink-3">「載せる」を選んだ作品がここに表示されます。</div>}
                </div>
            )}
        </div>
    );
};
